import React from "react";
import { Button, Menu, Fade } from "@material-ui/core";
import OpenInNew from "@material-ui/icons/OpenInNew";
import { InlineValueDisplayList } from "./InlineValueDisplayList";
import { DateDisplay } from "./DateDisplay";
import { camelToTitleCase } from "../utils";
import { EVENT_CARD_TITLES } from "../constants/index";
import colors from "../constants/colors";
import MoreVertIcon from "@material-ui/icons/MoreVert";

interface EventCardProps {
	//A string used as the title. Will be used to map a color
	//to the left border of the card.
	title: string;
	//Date to be displayed as 'EventDate'
	date: Date;
	//a method returning a react node to be used to view the message for the card
	messageViewer: (
		source: string,
		message: string,
		sid?: string,
		loadMessage?: (sid: string) => () => Promise<any>,
	) => React.ReactNode;
	//The message to be passed to message viewer when the message viewer is opened.
	message: string;
	//Function that will call into CLE to get message if message is empty.
	loadMessageFunction?: (sid: string) => () => Promise<any>;
	//A react node to be used for secondary actions for an event cards. Can be used to display an action specific to this card.
	secondaryAction?: React.ReactNode;
	//an object that contains all of the properties to be displayed
	innerCard: object;
	//A boolean passed in to tell whether or not this card is expanded.
	expanded: boolean;
	//A function passed in order to set parents expanded field for this card.
	setExpanded: (expanded) => void;
	//A function passed in by the page to navigate when clicking on the sid.
	detailsFunction?: (sid, open: boolean) => string;
	//The sid for the call or channel.
	sid?: string;
	// react node for buttons that link to an external url
	externalLinks?: React.ReactNode[];
}

export const borderColors = {
	taskRouter: colors.green400,
	runtimeDebugger: colors.red400,
	cloudFunction: colors.orange400,
	browserFunction: colors.indigo400,
	platformStatus: colors.purple400,
	flowExecutionStep: colors.yellow400,
	callEvent: colors.blue400,
	channelList: colors.pink400,
	callList: colors.teal400,
};

function getValueDisplayObjects(innerPropertiesObject) {
	return Object.keys(innerPropertiesObject).map((key) => {
		return {
			label: camelToTitleCase(key),
			node: innerPropertiesObject[key],
			row: true,
		};
	});
}

function createInlineValueDisplayList(innerCard) {
	return (
		<div style={{ fontSize: 14 }}>
			<InlineValueDisplayList
				shouldSkipEmptyValues={true}
				data-testid="box-display-list"
				displayItems={getValueDisplayObjects(innerCard)}
			></InlineValueDisplayList>
		</div>
	);
}

function sidDisplay(detailsFunction?: (sid, open: boolean) => string, sid?: string) {
	const style = {
		marginLeft: 6,
		paddingLeft: 6,
		fontSize: 14,
	};

	let newTabLink;
	if (detailsFunction) {
		style["cursor"] = "pointer";
		style["color"] = colors.blue400;
		style["textDecoration"] = "underline";
		newTabLink = (
			<span
				style={style}
				onClick={() => {
					window.open(detailsFunction!(sid, false));
				}}
			>
				<OpenInNew style={{ fontSize: 14 }}></OpenInNew>
			</span>
		);
	}

	if (sid) {
		return (
			<span>
				<span
					data-testid={`event-card-sid`}
					style={style}
					onClick={() => {
						if (detailsFunction) {
							window.history.pushState({}, "", detailsFunction(sid, true));
						}
					}}
				>
					{sid}
				</span>
				{newTabLink}
			</span>
		);
	}
}

/**
 * Accepts a title string, a date to be displayed as the EventDate,
 * a react node to be used to view the message for the card,
 * and an object that contains all of the properties to be displayed in
 * the inner card.
 * @param props
 */
export const EventCard = (props: EventCardProps) => {
	const [expanded, setLocalExpanded] = React.useState(props.expanded);
	const [messageSection, setMessageSection] = React.useState<React.ReactNode>(
		expanded ? props.messageViewer(props.title, props.message, props.sid, props.loadMessageFunction) : undefined,
	);

	function handleExpandClick() {
		props.setExpanded(!expanded);
		if (!messageSection) {
			setMessageSection(props.messageViewer(props.title, props.message, props.sid, props.loadMessageFunction));
		}
		setLocalExpanded(!expanded);
	}

	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	return (
		<div
			style={{
				borderRadius: 4,
				borderLeftWidth: 8,
				borderLeftStyle: "solid",
				borderLeftColor: borderColors[props.title],
				boxShadow: `-1px 1px 3px ${colors.gray500}`,
				margin: 5,
				padding: 8,
			}}
		>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
				<span>
					<span
						style={{
							fontSize: 11,
							color: colors.gray700,
							backgroundColor: colors.gray300,
							padding: "4px 8px",
							borderRadius: 4,
							fontWeight: 700,
							letterSpacing: ".1em",
						}}
						data-testid="card-title"
					>
						<span>{EVENT_CARD_TITLES[props.title] || props.title}</span>
						<span style={{ marginLeft: 6, paddingLeft: 6, borderLeft: `1px solid ${colors.gray500}` }}>
							<DateDisplay date={props.date} fontSize={11} />
						</span>
					</span>
					{sidDisplay(props.detailsFunction, props.sid)}
				</span>
				<span
					style={{
						fontSize: 11,
						fontWeight: 700,
						letterSpacing: ".15em",
						display: "flex",
						justifyContent: "space-between",
					}}
				>
					{props.externalLinks && props.externalLinks.length > 0 && (
						<div>
							<Button
								aria-controls="fade-menu"
								size="small"
								aria-haspopup="true"
								variant="outlined"
								color="primary"
								onClick={handleMenuOpen}
								style={{ marginRight: 8, marginBottom: "6px", height: 30 }}
							>
								<MoreVertIcon fontSize="default" />
							</Button>
							<Menu
								id="fade-menu"
								anchorEl={anchorEl}
								keepMounted
								open={open}
								onClose={handleMenuClose}
								TransitionComponent={Fade}
								onClick={handleMenuClose}
							>
								{props.externalLinks.map((x) => {
									return x;
								})}
							</Menu>
						</div>
					)}

					{props.secondaryAction && <span>{props.secondaryAction}</span>}
					<span>
						<Button
							size="small"
							variant="outlined"
							color={"primary"}
							onClick={handleExpandClick}
							data-testid="card-message-button"
							style={{ marginLeft: 8 }}
						>
							{expanded ? "Hide Message" : "View Message"}
						</Button>
					</span>
				</span>
			</div>
			{createInlineValueDisplayList(props.innerCard)}
			<div style={{ paddingTop: 8 }} hidden={!expanded} data-testid="card-message-viewer">
				{messageSection}
			</div>
		</div>
	);
};
